// @flow
import React, {
  useState,
} from 'react';

import Avatar from '@atlaskit/avatar';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import Button from '@atlaskit/button';
import EditorEditIcon from '@atlaskit/icon/glyph/editor/edit';
import EditorRemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import EditorLinkIcon from '@atlaskit/icon/glyph/editor/link';
import CameraIcon from '@atlaskit/icon/glyph/camera';

import * as utils from 'chronos-utils';
import {
  DateTime,
} from 'luxon';
import type {
  ID,
  WorklogGroup,
} from 'chronos-types';

import {
  Popup,
} from 'chronos-components';

import * as S from 'chronos-styled';

import {
  CalendarWorklogContainer,
  CalendarWorklogBody,
  CalendarWorklogBodyLeft,
  CalendarWorklogBodyRight,
  Edited,
  IssueKey,
  WorklogInfo,
  CounterBadge,
  IssueSummary,
} from './styled';

type Props = {
  /**
   * Worklog to render
   * */
  jiraUrl: string,
  worklogGroup: WorklogGroup,
  onRemoveWorklog: (worklogId: ID) => void,
  onEditWorklog: (worklogId: ID) => void,
  onAddWorklog: () => void,
  onShowScreenshotsViewer: () => void,
};

const CalendarWorklog: React.StatelessFunctionalComponent<Props> = ({
  jiraUrl,
  worklogGroup,
  onRemoveWorklog,
  onEditWorklog,
  onAddWorklog,
  onShowScreenshotsViewer,
}: Props): React.Node => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <CalendarWorklogContainer>
      <Popup
        place="row"
        isOpen={isOpen}
        setPopupVisible={() => {
          setIsOpen(!isOpen);
        }}
        handleOuterAction={(ev: any) => {
          ev.preventDefault();
          setIsOpen(false);
        }}
        trigger={(
          <CalendarWorklogBody>
            <CalendarWorklogBodyLeft>
              <IssueKey type={utils.getWorklogType(worklogGroup)}>
                {worklogGroup.issue.key}
              </IssueKey>
              <WorklogInfo>
                {utils.stj(worklogGroup.timeSpentSeconds)}
              </WorklogInfo>
            </CalendarWorklogBodyLeft>
            <CalendarWorklogBodyRight>
              <Avatar
                size="small"
                src={worklogGroup.author.avatarUrls['48x48']}
              />
              {worklogGroup.worklogs.length > 1 && (
                <CounterBadge>
                  x
                  {worklogGroup.worklogs.length}
                </CounterBadge>
              )}
            </CalendarWorklogBodyRight>
          </CalendarWorklogBody>
        )}
      >
        <S.PopupContent>
          {isOpen
            && (
              <S.PopupInner>
                <S.PopupHeader>
                  <S.Flex spacing={4}>
                    <S.JiraLink
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`${jiraUrl}/browse/${worklogGroup.issue.fields.project.key}`}
                    >
                      {worklogGroup.issue.fields.project.name}
                    </S.JiraLink>
                    <span>/</span>
                    <S.JiraLink
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`${jiraUrl}/browse/${worklogGroup.issue.key}`}
                    >
                      {worklogGroup.issue.key}
                    </S.JiraLink>
                  </S.Flex>
                  <IssueSummary>
                    {worklogGroup.issue.fields.summary}
                  </IssueSummary>
                </S.PopupHeader>
                <S.Flex
                  justify="space-between"
                  width="100%"
                  padding="4px 16px"
                >
                  <S.Flex width="200px">
                    Total time spent:
                    {' '}
                    {utils.stj(worklogGroup.timeSpentSeconds)}
                  </S.Flex>
                </S.Flex>
                <S.Flex
                  justify="space-between"
                  width="100%"
                  padding="4px 16px"
                >
                  <S.Flex
                    borderBottom="4px solid #172b4d"
                    width="84px"
                  >
                    <span
                      style={{
                        fontSize: '1.2em',
                        lineHeight: '28px',
                      }}
                    >
                      Worklogs
                    </span>
                    {worklogGroup.isScreenshotsExists && (
                      <Button
                        appearance="subtle-link"
                        onClick={() => {
                          onShowScreenshotsViewer(
                            worklogGroup.worklogs.map(w => w.id),
                          );
                        }}
                        spacing="none"
                        style={{
                          marginTop: '6px',
                          marginLeft: '5px',
                        }}
                      >
                        <CameraIcon size="small" />
                      </Button>
                    )}
                  </S.Flex>
                  <Button
                    iconBefore={<EditorAddIcon />}
                    appearance="primary"
                    onClick={() => {
                      onAddWorklog();
                    }}
                  >
                    Add New
                  </Button>
                </S.Flex>
                <S.Flex scroll style={{ maxHeight: 400, width: '100%' }}>
                  <S.Flex column width="100%">
                    {worklogGroup.worklogs.map(
                      worklog => (
                        <S.Flex column spacing={16} padding="12px" key={worklog.id} width="100%">
                          <S.Flex align="center" justify="space-between" width="100%" spacing={4}>
                            <S.Flex spacing={8} align="center">
                              <Avatar size="small" src={worklog.author.avatarUrls['32x32']} />
                              {worklog.author.displayName}
                              {' '}
                              logged work
                              {'  -  '}
                              {DateTime.fromISO(worklog.started).setZone(worklog.author.timeZone).toFormat('dd/MMM/yy t')}
                            </S.Flex>
                            <S.Flex spacing={4}>
                              {worklog.isScreenshotsExists && (
                                <Button
                                  appearance="subtle-link"
                                  onClick={() => {
                                    onShowScreenshotsViewer([worklog.id]);
                                  }}
                                  style={{
                                    marginTop: '5px',
                                  }}
                                  spacing="none"
                                >
                                  <CameraIcon size="small" />
                                </Button>
                              )}
                              <S.JiraLink
                                target="_blank"
                                rel="noopener noreferrer"
                                href={[
                                  jiraUrl,
                                  `/browse/${worklogGroup.issue.key}`,
                                  `?focusedWorklogId=${worklog.id}`,
                                  `&page=com.atlassian.jira.plugin.system.issuetabpanels%3Aworklog-tabpanel#worklog-${worklog.id}`,
                                ].join('')}
                              >
                                <EditorLinkIcon />
                              </S.JiraLink>
                              <Button
                                onClick={() => {
                                  onRemoveWorklog(worklog.id);
                                }}
                                spacing="none"
                              >
                                <EditorRemoveIcon />
                              </Button>
                              <Button
                                onClick={() => {
                                  onEditWorklog(worklog.id);
                                }}
                                spacing="none"
                              >
                                <EditorEditIcon />
                              </Button>
                            </S.Flex>
                          </S.Flex>
                          <S.Flex>
                            <span style={{ marginRight: 32, color: '#5e6c84' }}>
                              Time Spent:
                            </span>
                            {worklog.timeSpent}
                            {
                              worklog.created !== worklog.updated
                                && (
                                  <Edited>
                                    <span>
                                      edited
                                    </span>
                                  </Edited>
                                )
                            }
                          </S.Flex>
                          <S.Flex>
                            <span style={{ marginRight: 32, color: '#5e6c84' }}>
                              Comment:
                            </span>
                            {worklog.commentText}
                          </S.Flex>
                        </S.Flex>
                      ),
                    )}
                  </S.Flex>
                </S.Flex>
              </S.PopupInner>
            )
          }
        </S.PopupContent>
      </Popup>
    </CalendarWorklogContainer>
  );
};

export default CalendarWorklog;
